import { db } from "../../firebase";
import {
  collection, doc, getDocs, getDoc, addDoc, updateDoc, deleteDoc, query, where, orderBy, serverTimestamp, increment, limit as firestoreLimit, startAfter, documentId
} from "firebase/firestore";
import {
  QUESTIONS_FETCH_REQUEST, QUESTIONS_FETCH_SUCCESS, QUESTIONS_FETCH_FAILURE,
  QUESTION_CREATE_REQUEST, QUESTION_CREATE_SUCCESS, QUESTION_CREATE_FAILURE,
  QUESTION_UPDATE_REQUEST, QUESTION_UPDATE_SUCCESS, QUESTION_UPDATE_FAILURE,
  QUESTION_DELETE_REQUEST, QUESTION_DELETE_SUCCESS, QUESTION_DELETE_FAILURE,
  QUESTION_FETCH_BY_ID_REQUEST, QUESTION_FETCH_BY_ID_SUCCESS, QUESTION_FETCH_BY_ID_FAILURE,
  QUESTIONS_RESET
} from "./QuestionsTypes";
import { v4 as uuidv4 } from 'uuid';
import { deleteImage } from '../../util/imageUpload';
import { cleanDraftEntityMap, extractImageUrls } from '../../util/draftEntityCleanup';

// Reset
export const resetQuestions = () => ({ type: QUESTIONS_RESET });

// Fetch questions (optionally filter by subjectId, unitId, and/or topicId) (#24)
// Supports pagination via pageSize/lastDoc. When no pageSize is given, fetches all (backwards-compatible).
const QUESTIONS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export const fetchQuestions = ({ subjectId = null, unitId = null, topicId = null, pageSize = null, lastDoc = null, append = false } = {}) => {
  return async (dispatch, getState) => {
    try {
      // TTL staleness check: skip Firestore read if unfiltered data is fresh
      const hasFilters = !!(subjectId || unitId || topicId);
      if (!hasFilters && !append && !lastDoc) {
        const state = getState().questionBankQuestions;
        if (
          state.questions.length > 0 &&
          state.lastFetchedAt &&
          state.lastFetchParams === null &&
          Date.now() - state.lastFetchedAt < QUESTIONS_CACHE_TTL
        ) {
          return { success: true, questions: state.questions, lastVisible: null, hasMore: false };
        }
      }

      dispatch({ type: QUESTIONS_FETCH_REQUEST });

      const questionsRef = collection(db, "questionBankQuestions");
      const constraints = [orderBy("createdAt", "desc")];

      if (subjectId) constraints.unshift(where("subjectId", "==", subjectId));
      if (unitId) constraints.unshift(where("unitId", "==", unitId));
      if (topicId) constraints.unshift(where("topicId", "==", topicId));

      if (pageSize) constraints.push(firestoreLimit(pageSize));
      if (lastDoc) constraints.push(startAfter(lastDoc));

      const q = query(questionsRef, ...constraints);

      const snapshot = await getDocs(q);

      const questions = snapshot.docs.map(doc => ({
        questionId: doc.id,
        ...doc.data()
      })).filter(q => q.status !== 'archived');

      const fetchParams = hasFilters ? { subjectId, unitId, topicId } : null;
      if (append) {
        const existing = getState().questionBankQuestions.questions || [];
        dispatch({ type: QUESTIONS_FETCH_SUCCESS, payload: [...existing, ...questions], fetchParams });
      } else {
        dispatch({ type: QUESTIONS_FETCH_SUCCESS, payload: questions, fetchParams });
      }

      const lastVisible = snapshot.docs[snapshot.docs.length - 1] || null;
      return { success: true, questions, lastVisible, hasMore: pageSize ? snapshot.docs.length === pageSize : false };
    } catch (error) {
      dispatch({ type: QUESTIONS_FETCH_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};

// Fetch single question by ID
export const fetchQuestionById = (questionId) => {
  return async (dispatch) => {
    try {
      dispatch({ type: QUESTION_FETCH_BY_ID_REQUEST });

      const questionRef = doc(db, "questionBankQuestions", questionId);
      const questionDoc = await getDoc(questionRef);

      if (!questionDoc.exists()) {
        throw new Error("Question not found");
      }

      const questionData = questionDoc.data();
      if (questionData.status === 'archived') {
        throw new Error("Question not found");
      }

      const question = {
        questionId: questionDoc.id,
        ...questionData
      };
      dispatch({ type: QUESTION_FETCH_BY_ID_SUCCESS, payload: question });
      return { success: true, question };
    } catch (error) {
      dispatch({ type: QUESTION_FETCH_BY_ID_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};

/**
 * Generate a unique question code
 * Format: QST-{SUBJECT_CODE}-{NUMBER}
 * Example: QST-BIO-00123
 */
const generateQuestionCode = async (subjectId, subjectName) => {
  try {
    // Get subject code (first 3 letters of subject name, uppercase)
    const subjectCode = subjectName.substring(0, 3).toUpperCase();

    // Query existing questions for this subject to find the max code number
    const questionsRef = collection(db, "questionBankQuestions");
    const q = query(questionsRef, where("subjectId", "==", subjectId));
    const snapshot = await getDocs(q);

    // Find the highest existing code number to avoid duplicates after deletions
    let maxNumber = 0;
    snapshot.docs.forEach(docSnap => {
      const code = docSnap.data().questionCode;
      if (code) {
        const match = code.match(/QST-[A-Z]{3}-(\d+)$/);
        if (match) {
          const num = parseInt(match[1], 10);
          if (num > maxNumber) maxNumber = num;
        }
      }
    });

    const nextNumber = maxNumber + 1;
    const paddedNumber = nextNumber.toString().padStart(6, '0');

    // Generate unique code
    const questionCode = `QST-${subjectCode}-${paddedNumber}`;

    return questionCode;
  } catch (error) {
    // Fallback to UUID-based code if generation fails
    return `QST-${uuidv4().substring(0, 8).toUpperCase()}`;
  }
};

// Create question
export const createQuestion = (questionData, userEmail, subjectName = null) => {
  return async (dispatch) => {
    try {
      dispatch({ type: QUESTION_CREATE_REQUEST });

      // Add unique IDs to choices if not provided
      const choicesWithIds = questionData.choices.map(choice => ({
        choiceId: choice.choiceId || uuidv4(),
        text: choice.text
      }));

      // Clean Draft.js entityMaps (remove stale/unreferenced entities)
      const cleanedQuestionText = cleanDraftEntityMap(questionData.questionText);
      const cleanedExplanationText = cleanDraftEntityMap(questionData.explanationText);
      const cleanedLessonReference = questionData.lessonReference
        ? cleanDraftEntityMap(questionData.lessonReference)
        : null;
      const cleanedChoices = choicesWithIds.map(choice => ({
        ...choice,
        text: choice.text ? cleanDraftEntityMap(choice.text) : choice.text
      }));

      // Generate unique question code
      const questionCode = await generateQuestionCode(questionData.subjectId, subjectName || 'GEN');

      const questionsRef = collection(db, "questionBankQuestions");
      const newQuestion = {
        // Unique Question Code (for tracking)
        questionCode: questionCode,

        // Question Content
        questionText: cleanedQuestionText,

        // Choices
        choices: cleanedChoices,
        choiceType: "multiple_choice",
        correctAnswer: questionData.correctAnswer || [],

        // Explanation
        explanationText: cleanedExplanationText,

        // Categorization
        subjectId: questionData.subjectId,
        unitId: questionData.unitId,
        topicId: questionData.topicId || null,
        difficulty: questionData.difficulty || "medium",
        quality: questionData.quality || "simple",
        tags: questionData.tags || [],

        // Passage reference (if passage-based)
        ...(questionData.passageId && { passageId: questionData.passageId, isPassageBased: true }),

        // Reference
        lessonReference: cleanedLessonReference,

        // Metadata
        createdBy: userEmail,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        source: questionData.source || "manual",
        bulkUploadId: questionData.bulkUploadId || null,
        status: questionData.status || "active",
        isActive: true,

        // Analytics
        timesAnswered: 0,
        timesCorrect: 0,
        averageTimeSpent: 0,
        lastUsedAt: null
      };

      const docRef = await addDoc(questionsRef, newQuestion);

      // Increment passage questionCount atomically if this question is linked to a passage
      if (questionData.passageId) {
        const passagesRef = collection(db, "questionBankPassages");
        const passageQuery = query(passagesRef, where("passageId", "==", questionData.passageId));
        const passageSnapshot = await getDocs(passageQuery);
        if (!passageSnapshot.empty) {
          const passageDocRef = doc(db, "questionBankPassages", passageSnapshot.docs[0].id);
          await updateDoc(passageDocRef, { questionCount: increment(1) });
        }
      }

      const createdQuestion = {
        questionId: docRef.id,
        ...newQuestion,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      dispatch({ type: QUESTION_CREATE_SUCCESS, payload: createdQuestion });
      return { success: true, questionId: docRef.id, questionCode: questionCode };
    } catch (error) {
      dispatch({ type: QUESTION_CREATE_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};

// Update question
export const updateQuestion = (questionId, questionData) => {
  return async (dispatch) => {
    try {
      dispatch({ type: QUESTION_UPDATE_REQUEST });

      const questionRef = doc(db, "questionBankQuestions", questionId);

      // Fetch current question to detect changes that require analytics reset
      const currentDoc = await getDoc(questionRef);
      const currentData = currentDoc.exists() ? currentDoc.data() : {};

      // Collect OLD image URLs from all rich text fields
      const oldImageUrls = new Set([
        ...extractImageUrls(currentData.questionText),
        ...extractImageUrls(currentData.explanationText),
        ...extractImageUrls(currentData.lessonReference),
        ...(currentData.choices || []).flatMap(c => extractImageUrls(c.text))
      ]);

      // Clean Draft.js entityMaps (remove stale/unreferenced entities)
      if (questionData.questionText) {
        questionData.questionText = cleanDraftEntityMap(questionData.questionText);
      }
      if (questionData.explanationText) {
        questionData.explanationText = cleanDraftEntityMap(questionData.explanationText);
      }
      if (questionData.lessonReference) {
        questionData.lessonReference = cleanDraftEntityMap(questionData.lessonReference);
      }
      if (questionData.choices) {
        questionData.choices = questionData.choices.map(choice => ({
          ...choice,
          text: choice.text ? cleanDraftEntityMap(choice.text) : choice.text
        }));
      }

      // Collect NEW image URLs from cleaned data
      const newImageUrls = new Set([
        ...extractImageUrls(questionData.questionText),
        ...extractImageUrls(questionData.explanationText),
        ...extractImageUrls(questionData.lessonReference),
        ...(questionData.choices || []).flatMap(c => extractImageUrls(c.text))
      ]);

      // Delete orphaned images (fire-and-forget)
      const orphanedUrls = [...oldImageUrls].filter(url => !newImageUrls.has(url));
      if (orphanedUrls.length > 0) {
        Promise.all(orphanedUrls.map(url => deleteImage(url)))
          .catch(err => console.warn('Non-critical: failed to clean up orphaned images', err));
      }

      // Add unique IDs to choices if not provided
      const choicesWithIds = questionData.choices?.map(choice => ({
        choiceId: choice.choiceId || uuidv4(),
        text: choice.text
      }));

      const updateData = {
        ...questionData,
        ...(choicesWithIds && { choices: choicesWithIds }),
        updatedAt: serverTimestamp()
      };

      // Reset analytics if correctAnswer changed
      const oldCorrectAnswer = JSON.stringify(currentData.correctAnswer || []);
      const newCorrectAnswer = JSON.stringify(questionData.correctAnswer || []);
      if (oldCorrectAnswer !== newCorrectAnswer) {
        updateData.timesAnswered = 0;
        updateData.timesCorrect = 0;
        updateData.averageTimeSpent = 0;
      }

      // Reset analytics if subject or unit changed (question is recategorized)
      const subjectChanged = currentData.subjectId && questionData.subjectId && currentData.subjectId !== questionData.subjectId;
      const unitChanged = currentData.unitId && questionData.unitId && currentData.unitId !== questionData.unitId;
      if (subjectChanged || unitChanged) {
        updateData.timesAnswered = 0;
        updateData.timesCorrect = 0;
        updateData.averageTimeSpent = 0;
      }

      await updateDoc(questionRef, updateData);

      const updatedQuestion = {
        questionId,
        ...questionData,
        ...(choicesWithIds && { choices: choicesWithIds }),
        updatedAt: new Date().toISOString()
      };

      dispatch({ type: QUESTION_UPDATE_SUCCESS, payload: updatedQuestion });
      return { success: true };
    } catch (error) {
      dispatch({ type: QUESTION_UPDATE_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};

// Archive question (soft-delete)
export const deleteQuestion = (questionId) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: QUESTION_DELETE_REQUEST });

      const userEmail = getState().loginAuth?.userDetails?.email || 'unknown';

      const questionRef = doc(db, "questionBankQuestions", questionId);
      const questionDoc = await getDoc(questionRef);
      const questionData = questionDoc.exists() ? questionDoc.data() : null;

      if (!questionDoc.exists()) {
        throw new Error("Question not found.");
      }

      // Soft-delete: mark as archived instead of removing from Firestore
      await updateDoc(questionRef, {
        status: 'archived',
        isActive: false,
        archivedAt: Date.now(),
        archivedBy: userEmail
      });

      // Decrement passage questionCount atomically if this question was linked to a passage
      if (questionData?.passageId) {
        const passagesRef = collection(db, "questionBankPassages");
        const passageQuery = query(passagesRef, where("passageId", "==", questionData.passageId));
        const passageSnapshot = await getDocs(passageQuery);
        if (!passageSnapshot.empty) {
          const passageDocRef = doc(db, "questionBankPassages", passageSnapshot.docs[0].id);
          await updateDoc(passageDocRef, { questionCount: increment(-1) });
        }
      }

      dispatch({ type: QUESTION_DELETE_SUCCESS, payload: questionId });
      return { success: true };
    } catch (error) {
      dispatch({ type: QUESTION_DELETE_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};

// Batch-delete (archive) all active questions with a given bulkUploadId
export const deleteQuestionsBatch = (bulkUploadId) => {
  return async (dispatch, getState) => {
    try {
      const userEmail = getState().loginAuth?.userDetails?.email || 'unknown';
      const questionsRef = collection(db, "questionBankQuestions");
      const q = query(
        questionsRef,
        where("bulkUploadId", "==", bulkUploadId),
        where("status", "==", "active")
      );
      const snapshot = await getDocs(q);

      let deletedCount = 0;
      let failedCount = 0;

      for (const docSnap of snapshot.docs) {
        try {
          const questionData = docSnap.data();
          const questionRef = doc(db, "questionBankQuestions", docSnap.id);
          await updateDoc(questionRef, {
            status: 'archived',
            isActive: false,
            archivedAt: Date.now(),
            archivedBy: userEmail
          });

          // Decrement passage questionCount if linked to a passage
          if (questionData.passageId) {
            const passagesRef = collection(db, "questionBankPassages");
            const passageQuery = query(passagesRef, where("passageId", "==", questionData.passageId));
            const passageSnapshot = await getDocs(passageQuery);
            if (!passageSnapshot.empty) {
              const passageDocRef = doc(db, "questionBankPassages", passageSnapshot.docs[0].id);
              await updateDoc(passageDocRef, { questionCount: increment(-1) });
            }
          }

          deletedCount++;
        } catch (err) {
          console.error(`Failed to archive question ${docSnap.id}:`, err);
          failedCount++;
        }
      }

      return { success: true, deletedCount, failedCount };
    } catch (error) {
      console.error('deleteQuestionsBatch error:', error);
      return { success: false, error: error.message, deletedCount: 0, failedCount: 0 };
    }
  };
};

// Update question order within a passage
export const updateQuestionOrder = (passageId, orderedQuestionIds) => {
  return async (dispatch) => {
    try {
      // Update each question with its new order
      const updates = orderedQuestionIds.map((questionId, index) => {
        const questionRef = doc(db, "questionBankQuestions", questionId);
        return updateDoc(questionRef, {
          passageOrder: index,
          updatedAt: serverTimestamp()
        });
      });

      await Promise.all(updates);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };
};

// Migrate existing questions to have proper questionCodes
export const migrateQuestionCodes = (subjects) => {
  return async () => {
    try {
      const questionsRef = collection(db, "questionBankQuestions");
      const snapshot = await getDocs(questionsRef);

      // Group questions by subjectId
      const questionsBySubject = {};
      snapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        const subjectId = data.subjectId;
        if (!questionsBySubject[subjectId]) {
          questionsBySubject[subjectId] = [];
        }
        questionsBySubject[subjectId].push({
          docId: docSnap.id,
          ...data
        });
      });

      let updatedCount = 0;
      let errorCount = 0;

      // Process each subject group
      for (const subjectId of Object.keys(questionsBySubject)) {
        const questions = questionsBySubject[subjectId];
        const subject = subjects?.find(s => s.subjectId === subjectId);
        const subjectCode = subject?.name?.substring(0, 3).toUpperCase() || 'GEN';

        // Sort by createdAt to maintain order
        questions.sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || a.createdAt || 0;
          const bTime = b.createdAt?.toMillis?.() || b.createdAt || 0;
          return aTime - bTime;
        });

        // Update each question with new code
        for (let i = 0; i < questions.length; i++) {
          const question = questions[i];
          const paddedNumber = (i + 1).toString().padStart(6, '0');
          const newQuestionCode = `QST-${subjectCode}-${paddedNumber}`;

          try {
            const questionRef = doc(db, "questionBankQuestions", question.docId);
            await updateDoc(questionRef, { questionCode: newQuestionCode });
            updatedCount++;
          } catch (err) {
            console.error(`Failed to update question ${question.docId}:`, err);
            errorCount++;
          }
        }
      }

      return {
        success: true,
        updatedCount,
        errorCount,
        message: `Updated ${updatedCount} questions. Errors: ${errorCount}`
      };
    } catch (error) {
      console.error('Migration error:', error);
      return { success: false, error: error.message };
    }
  };
};

// Fetch multiple questions by their IDs (for feedback export)
export const fetchQuestionsByIds = (questionIds) => {
  return async () => {
    try {
      if (!questionIds || questionIds.length === 0) {
        return { success: true, questions: {} };
      }

      const questionsRef = collection(db, "questionBankQuestions");
      const questionsMap = {};

      // Firestore 'in' queries support max 10 values, so batch them
      const batches = [];
      for (let i = 0; i < questionIds.length; i += 10) {
        batches.push(questionIds.slice(i, i + 10));
      }

      for (const batch of batches) {
        const q = query(questionsRef, where(documentId(), "in", batch));
        const snapshot = await getDocs(q);
        snapshot.docs.forEach(doc => {
          questionsMap[doc.id] = { questionId: doc.id, ...doc.data() };
        });
      }

      return { success: true, questions: questionsMap };
    } catch (error) {
      console.error('fetchQuestionsByIds error:', error);
      return { success: false, error: error.message };
    }
  };
};
